// Correlation verdict card — renders the cached heavy-stat suite from
// GET /api/safety/verdict (permutation p-values, observed-vs-expected in-run
// counts, rate ratios). Unlike the descriptive panels this does NOT react to
// filters: it is computed once per corridor+crash-dataset over the full
// unfiltered data, and says so.
//
// Reading the verdict correctly: the test does NOT expect a crash at every
// deficiency. It asks one question — do crashes concentrate at deficiency
// locations MORE than random placement would, given how much of the corridor
// is deficient? "At chance" means the observed count is about what random
// placement yields for runs covering that much roadway, not "no crashes at
// deficiencies".
import React from 'react';

const CHIP = {
  associated: { label: 'Above chance', className: 'safety-chip safety-chip-bad' },
  not_associated: { label: 'At chance', className: 'safety-chip safety-chip-ok' },
  underpowered: { label: 'Too few crashes', className: 'safety-chip safety-chip-warn' },
  no_station_data: { label: 'No data', className: 'safety-chip safety-chip-neutral' },
  descriptive: { label: 'Descriptive', className: 'safety-chip safety-chip-neutral' },
};

const SUBSET_LABEL = {
  non_animal: 'non-animal',
  wet_surface: 'wet/snow surface',
  severe_embankment: 'severe embankment',
};

const fmt = (v, digits = 3) => (v === null || v === undefined ? '—' : Number(v).toFixed(digits));

export default function VerdictCard({ verdict, loading, error, onRecompute, recomputing }) {
  if (loading) return <div className="safety-muted">Computing correlation verdict…</div>;
  if (error) return <div className="safety-error">Verdict unavailable: {error}</div>;
  if (!verdict) return null;

  return (
    <div className="safety-verdict">
      <p className="safety-verdict-text">{verdict.overall?.text}</p>
      <p className="safety-muted safety-verdict-power">
        Supporting evidence only — correlations are identified per crash in Insights
        (a crash at a deficiency IS the finding). This table asks the corridor-wide
        question: do crashes concentrate at deficiency locations more than random
        placement would, given how much roadway each analysis flags? It does not
        expect a crash at every deficiency.
        {verdict.overall?.powerNote ? ` ${verdict.overall.powerNote}` : ''}
      </p>
      <div className="safety-table-scroll">
      <table className="safety-table">
        <thead>
          <tr>
            <th>Analysis</th><th>Verdict</th><th>Crashes tested</th>
            <th>In runs (obs / exp)</th><th>p (count)</th>
            <th>Mean nearest (mi)</th><th>p (distance)</th>
            <th>Rate ratio</th><th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(verdict.perAnalysis || {}).map(([analysis, v]) => {
            const chip = CHIP[v.verdict] || CHIP.underpowered;
            return (
              <tr key={analysis}>
                <td>{analysis}</td>
                <td><span className={chip.className}>{chip.label}</span></td>
                <td>{v.nCrashes}{v.crashSubset && v.crashSubset !== 'non_animal'
                  ? ` (${SUBSET_LABEL[v.crashSubset] || v.crashSubset})` : ''}</td>
                <td>{v.nInRuns != null ? `${v.nInRuns} / ~${v.expectedInRuns}` : '—'}</td>
                <td>{fmt(v.countP)}</td>
                <td>{v.meanNearestMi != null
                  ? `${fmt(v.meanNearestMi)} vs ${fmt(v.expectedMeanMi)}` : '—'}</td>
                <td>{fmt(v.pValue)}</td>
                <td>{fmt(v.rateRatio, 2)}</td>
                <td className="safety-verdict-note">
                  {analysis === 'GLR' && v.temporalMatches != null
                    ? `${v.temporalMatches} crash${v.temporalMatches === 1 ? '' : 'es'} inside glare impact ranges`
                    : v.note || '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <div className="safety-verdict-foot">
        <span className="safety-muted">
          Computed {verdict.computedAt ? verdict.computedAt.slice(0, 10) : '—'} on
          dataset {verdict.datasetVersion} · full corridor, animal strikes excluded ·
          5,000-draw permutation test
        </span>
        <button type="button" className="safety-btn" onClick={onRecompute} disabled={recomputing}>
          {recomputing ? 'Recomputing…' : 'Recompute'}
        </button>
      </div>
    </div>
  );
}
